import React, { ReactNode } from 'react';

interface Props {
    /** A fully-formed 40x40 badge — `StudioCardIcon` for results, a local badge for placeholders. */
    icon: ReactNode;
    title: string;
    description?: string;
    /** Absent for the idle-state placeholders, which carry no CTA. */
    onClick?: () => void;
    testId: string;
}

const SHELL = 'flex w-full items-center gap-4 rounded-[12px] border border-filterV2Border bg-white p-4 text-left';

/**
 * One card in the search panel — the same Figma shell serves the idle-state placeholders and the
 * submitted results.
 *
 * Rendered as a `<button>` only when it does something. A placeholder is a `<div>`, so it never
 * enters the tab order and never offers an affordance it cannot honour.
 */
function LandingSearchCard({ icon, title, description, onClick, testId }: Props) {
    const body = (
        <>
            {icon}
            <div className="flex min-w-px flex-[1_0_0] flex-col gap-1 break-words">
                <p className="w-full font-sm font-semibold leading-[1.2] text-filterV2TextPrimary">{title}</p>
                {description && <p className="w-full font-xs font-normal text-navV2TextMuted">{description}</p>}
            </div>
        </>
    );

    if (!onClick) {
        return (
            <div data-testid={testId} className={SHELL}>
                {body}
            </div>
        );
    }

    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`${SHELL} transition-colors duration-200 hover:border-landingAccent hover:bg-filterV2PrimaryBg`}
        >
            {body}
        </button>
    );
}

export default LandingSearchCard;
